import React from 'react';

import { commodityApis } from '@apis/next/commodityApis';
import { Button } from '@components/Button';
import { Layout } from '@components/Layout';
import { PaginationWithSearchQuery } from '@components/PaginationWithSearchQuery';
import { SortButtons } from '@components/sort/SortButtons';
import { CommodityCard } from '@components/utilities/Commodity/CommodityCard';
import { CommoditySearch } from '@components/utilities/Commodity/CommoditySearch';
import { Container } from '@components/utilities/Contact/Container';
import { VerticalSpace } from '@components/VerticalSpace';
import { useSearch } from '@hooks/next/useSearch';
import { CommoditySearchQuery } from '@models/next/Commodity';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import clsx from 'clsx';
import { stringify } from 'query-string';
import { Link } from 'react-router-dom';

const strings = createStrings({
  ja: {
    title: '輸入商品一覧',
    addCommodity: '新規登録',
    noData: () => {
      const classes = useCommonStyles();
      return (
        <>
          まだ商品を登録していません。
          <br />
          <span>
            <Link className={classes.link} to={url('/utilities/Commodity/AddCommodity')}>
              「新規登録」
            </Link>
            をクリックして商品を登録してください。
          </span>
        </>
      );
    },
    noSearchResult: () => (
      <>
        検索条件に当てはまるデータが見つかりませんでした。
        <br />
        検索条件を変えて検索してみてください。
      </>
    ),
    sortByUseCountDesc: '利用回数順',
    sortByCreatedAtAsc: '登録順',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  body: {
    ...common.flexColumn,
    flex: 1,
  },
  commodityGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '1rem',
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  noResultHint: {
    ...common.textGray500,
    ...common.middleText,
    ...common.textAlignCenter,
    margin: '200px 1rem',
    [theme.breakpoints.down('xs')]: {
      margin: '1rem',
    },
  },
}));

export const Commodities: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { search, updateSearch } = useSearch(CommoditySearchQuery);

  const queries = {
    ...search,
    page_size: search.page_size ?? 10,
  } satisfies CommoditySearchQuery;

  const commodities = commodityApis.useGet('/commodity', {
    queries,
  });

  const isNoSearchResult =
    (search.commodity_no && search.commodity_no?.length > 0) ||
    (search.name_en && search.name_en?.length > 0);

  return (
    <Layout
      loading={commodities.isLoading}
      errors={[commodities.error]}
      title={strings.title}
      breadcrumbs={['/utilities/Dashboard', '/utilities/Commodity/Commodities']}
    >
      <div className={classes.body}>
        <Container>
          <VerticalSpace height={16} />
          <div className={clsx(classes.pageTitleText, classes.textPrimary)}>{strings.title}</div>
          <VerticalSpace height={16} />

          <CommoditySearch />
          <VerticalSpace height={16} />

          <div className={clsx(classes.flexInline, classes.justifyBetween)}>
            <Link to={url('/utilities/Commodity/AddCommodity')}>
              <Button
                text={strings.addCommodity}
                color="primary"
                size="small"
                style={{ width: 133 }}
              />
            </Link>

            <div className={classes.flex1} />

            <SortButtons
              currentSortKey={queries.ordering?.[0] ?? '-use_count'}
              items={[
                {
                  title: strings.sortByUseCountDesc,
                  sortKey: '-use_count',
                },
                {
                  title: strings.sortByCreatedAtAsc,
                  sortKey: '-created_at',
                },
              ]}
              onSortKeyChanged={(ordering) => updateSearch({ ordering: [ordering] })}
            />
          </div>

          <VerticalSpace height={30} />

          {!!commodities.data?.count && (
            <>
              <div className={classes.commodityGrid}>
                {commodities.data?.results.map((commodity) => (
                  <CommodityCard
                    key={commodity.id}
                    commodity={commodity}
                    editUrl={
                      url('/utilities/Commodity/CommodityDetails') +
                      '?' +
                      stringify({ id: commodity.id, action: 'edit' })
                    }
                    detailsUrl={
                      url('/utilities/Commodity/CommodityDetails') +
                      '?' +
                      stringify({ id: commodity.id })
                    }
                  />
                ))}
              </div>

              <VerticalSpace height={30} />

              <div className={clsx(classes.flexInline, classes.justifyEnd)}>
                <PaginationWithSearchQuery query={commodities} pageSize={queries.page_size} />
              </div>
            </>
          )}

          {commodities.data?.count === 0 && (
            <div className={classes.noResultHint}>
              {isNoSearchResult ? strings.noSearchResult() : <strings.noData />}
            </div>
          )}

          <VerticalSpace height={46} />
        </Container>
      </div>
    </Layout>
  );
};
